/* eslint-disable  @typescript-eslint/no-explicit-any */
import { useContext, useState } from "react";
import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import CodeMirror from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";
import { tfidHighlightExtension } from "utils/TfidCodemirrorExtension";
import { EnvironmentSelector } from "components/EnvironmentSelector";
import type { IEnvironment } from "../../types/EnvironmentTypes";
import { useRegistryService } from "../../services/registry/RegistryService";
import { AppContext } from "utils/AppContext";

export interface CloneEntitiesDialogProps {
  visible: boolean;
  onClose: () => void;
}

export const CloneEntitiesDialog = ({ visible, onClose }: CloneEntitiesDialogProps) => {
  const registryService = useRegistryService();
  const appContext = useContext(AppContext);

  const [sourceEnvironment, setSourceEnvironment] = useState<IEnvironment | undefined>(undefined);
  const [entityIds, setEntityIds] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [recursive, setRecursive] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const [output, setOutput] = useState("");

  const targetEnvironment = appContext?.selectedEnvironment;

  const handleAddChip = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const trimmed = inputValue.trim();
      if (trimmed && !entityIds.includes(trimmed)) {
        setEntityIds([...entityIds, trimmed]);
      }
      setInputValue("");
    }
  };

  const handleDeleteChip = (idToDelete: string) => {
    setEntityIds(entityIds.filter((id) => id !== idToDelete));
  };

  const getSourceBaseURL = (): string => {
    let url = sourceEnvironment?.baseURL || "";
    if (url.endsWith("/")) {
      url = url.slice(0, -1);
    }
    return url;
  };

  const getTargetBaseURL = (): string => {
    let url = targetEnvironment?.baseURL || "";
    if (url.endsWith("/")) {
      url = url.slice(0, -1);
    }
    return url;
  };

  const handleClone = async () => {
    if (entityIds.length === 0 || !sourceEnvironment || !targetEnvironment) return;

    setIsCloning(true);
    setOutput("");

    try {
      const result = await registryService.cloneDataEntities(entityIds, getSourceBaseURL(), getTargetBaseURL(), recursive);
      setOutput(JSON.stringify(result.clonedEntityIds, null, 2));
    } catch (e: any) {
      setOutput(e.debugMessage || e.message || "Clone operation failed.");
    } finally {
      setIsCloning(false);
    }
  };

  const handleClose = () => {
    setSourceEnvironment(undefined);
    setEntityIds([]);
    setInputValue("");
    setRecursive(false);
    setOutput("");
    setIsCloning(false);
    onClose();
  };

  const canClone = entityIds.length > 0 && sourceEnvironment && targetEnvironment && !isCloning;

  return (
    <Dialog open={visible} onClose={handleClose} PaperProps={{ sx: { width: "100%", maxWidth: "700px!important" } }}>
      <DialogTitle>Clone Data Entities</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px!important" }}>
        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          Source Environment
        </Typography>
        <EnvironmentSelector
          id="clone-source"
          selectedEnvironment={sourceEnvironment}
          onEnvironmentSelected={setSourceEnvironment}
          minStage={8}
        />

        <Typography variant="subtitle2">Target Environment</Typography>
        <TextField
          value={targetEnvironment?.name || "No environment selected"}
          variant="filled"
          disabled
          fullWidth
          sx={{ background: "white" }}
        />

        <Typography variant="subtitle2">Entity IDs</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
          {entityIds.map((id) => (
            <Chip key={id} label={id} onDelete={() => handleDeleteChip(id)} size="small" />
          ))}
        </Box>
        <TextField
          fullWidth
          label="Add TFID and press Enter"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleAddChip}
          variant="outlined"
          size="small"
          disabled={isCloning}
        />

        <FormControlLabel
          control={<Checkbox checked={recursive} onChange={(e) => setRecursive(e.target.checked)} disabled={isCloning} />}
          label="Recursive (follow TFID references)"
        />

        {isCloning && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Cloning entities...
            </Typography>
          </Box>
        )}

        {output && (
          <CodeMirror value={output} editable={false} height="200px" extensions={[json(), tfidHighlightExtension]} />
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleClone} disabled={!canClone}>
          Clone
        </Button>
        <Button variant="contained" color="error" onClick={handleClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};
